import { useEffect, useMemo, useState } from "react";
import {
    Activity, ArrowDownCircle, ArrowUpCircle, Ban, Calendar, ChevronRight,
    CircleDollarSign, Gauge, Landmark, Loader2, PlayCircle, RefreshCw,
    ShieldAlert, TimerReset, TrendingDown, TrendingUp, Waves,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

function money(n, cur = "USD") {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString("es-MX", {
        style: "currency", currency: cur, maximumFractionDigits: 0,
    });
}

const ESTADO_LABELS = {
    ratificada: "Ratificada",
    disponible: "Disponible",
    en_availability: "En availability",
    dispuesta: "Dispuesta (legacy)",
    en_winddown: "En winddown",
    amortizando: "Amortizando (legacy)",
    en_default: "En default",
    reestructurada: "Reestructurada",
    pagada: "Pagada",
};
const ESTADO_COLOR = {
    ratificada: "text-muted-foreground",
    disponible: "text-[color:var(--brand-dark-gold)]",
    en_availability: "text-[color:var(--brand-exito)]",
    dispuesta: "text-[color:var(--brand-exito)]",
    en_winddown: "text-[color:var(--brand-dark-gold)]",
    amortizando: "text-[color:var(--brand-dark-gold)]",
    en_default: "text-[color:var(--brand-coral)]",
    pagada: "text-muted-foreground",
    reestructurada: "text-[color:var(--brand-dark-gold)]",
};
const ORDEN_CICLO = [
    "ratificada", "disponible", "en_availability", "en_winddown", "pagada",
];

function CicloVidaTimeline({ facility }) {
    const estado = facility?.estado || "ratificada";
    const idx = ORDEN_CICLO.indexOf(estado === "dispuesta" ? "en_availability"
                                      : estado === "amortizando" ? "en_winddown" : estado);
    return (
        <div className="flex items-center gap-2 flex-wrap"
            data-testid="estado-timeline-facility">
            {ORDEN_CICLO.map((s, i) => {
                const done = i <= idx && idx >= 0;
                const active = i === idx;
                return (
                    <div key={s} className="flex items-center gap-2">
                        <div className={`px-3 py-1.5 rounded-sm border text-[11px] uppercase tracking-widest
                            ${active ? "border-[color:var(--brand-dark-gold)] bg-[color:var(--brand-gold)]/15 font-semibold" : done ? "border-[color:var(--brand-exito)]/40 text-[color:var(--brand-exito)]" : "border-border text-muted-foreground"}`}>
                            {ESTADO_LABELS[s] || s}
                        </div>
                        {i < ORDEN_CICLO.length - 1 && (
                            <ChevronRight className={`h-3 w-3 ${done ? "text-[color:var(--brand-exito)]" : "text-muted"}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function CapacidadGauge({ cap, moneda }) {
    if (!cap) return null;
    const comprometido = cap.monto_comprometido || 0;
    const dispuesto = cap.monto_dispuesto_actual || 0;
    const reciclable = cap.monto_amortizado_reciclable || 0;
    const disponible = cap.monto_disponible_real || 0;
    const util_pct = comprometido > 0 ? (dispuesto / comprometido) * 100 : 0;

    return (
        <div className="rounded-md border border-border bg-card p-6" data-testid="capacidad-gauge">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                    Capacidad de disposición
                </h3>
                <div className="text-[11px] text-muted-foreground tabular">
                    calc: {cap.calculado_at?.slice(0, 16)?.replace("T", " ")}
                </div>
            </div>

            {/* Barra de utilización */}
            <div className="mb-6">
                <div className="flex justify-between text-[11px] uppercase tracking-widest mb-2">
                    <span className="text-muted-foreground">Utilización</span>
                    <span className="font-semibold tabular">{util_pct.toFixed(1)}%</span>
                </div>
                <div className="h-3 rounded-sm bg-muted overflow-hidden flex">
                    <div className="bg-[color:var(--brand-charcoal)]"
                        style={{ width: `${Math.min(util_pct, 100)}%` }} />
                    {reciclable > 0 && (
                        <div className="bg-[color:var(--brand-dark-gold)]"
                            style={{ width: `${(reciclable / comprometido) * 100}%` }} />
                    )}
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>0</span>
                    <span>Comprometido: {money(comprometido, moneda)}</span>
                </div>
            </div>

            {/* Componentes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-sm border border-border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                        <Landmark className="h-3 w-3" /> Dispuesto actual
                    </div>
                    <div className="text-xl font-medium mt-1">{money(dispuesto, moneda)}</div>
                </div>
                <div className="rounded-sm border border-border p-3 bg-[color:var(--brand-gold)]/10">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                        <Waves className="h-3 w-3 text-[color:var(--brand-dark-gold)]" /> Amortizado reciclable
                    </div>
                    <div className="text-xl font-medium mt-1 text-[color:var(--brand-dark-gold)]">
                        {money(reciclable, moneda)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">consumible primero</div>
                </div>
                <div className="rounded-sm border border-border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" /> Cushion BB
                    </div>
                    <div className={`text-xl font-medium mt-1 ${cap.cushion_borrowing_base < 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                        {money(cap.cushion_borrowing_base, moneda)}
                    </div>
                </div>
                <div className={`rounded-sm border p-3 ${disponible > 0 ? "border-[color:var(--brand-exito)]/40 bg-[color:var(--brand-exito)]/5" : "border-[color:var(--brand-coral)]/40 bg-[color:var(--brand-coral)]/5"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                        <CircleDollarSign className="h-3 w-3" /> Disponible real
                    </div>
                    <div className={`text-xl font-semibold mt-1 ${disponible > 0 ? "text-[color:var(--brand-exito)]" : "text-[color:var(--brand-coral)]"}`}>
                        {money(disponible, moneda)}
                    </div>
                    {cap.motivo_bloqueo && (
                        <div className="text-[10px] text-[color:var(--brand-coral)] mt-1 flex items-center gap-1"
                            data-testid="disposicion-motivo-bloqueo">
                            <Ban className="h-3 w-3" /> {cap.motivo_bloqueo}
                        </div>
                    )}
                </div>
            </div>

            {(cap.covenant_lock || cap.availability_lock) && (
                <div className="mt-4 flex flex-wrap gap-2">
                    {cap.covenant_lock && (
                        <span className="badge-error flex items-center gap-1">
                            <ShieldAlert className="h-3 w-3" /> Covenant lock activo
                        </span>
                    )}
                    {cap.availability_lock && (
                        <span className="badge-gold flex items-center gap-1">
                            <TimerReset className="h-3 w-3" /> Availability lock
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function NuevaDisposicionDialog({ facility, cap, cuentas, onDone }) {
    const [open, setOpen] = useState(false);
    const [monto, setMonto] = useState("");
    const [cuentaId, setCuentaId] = useState("");
    const [motivo, setMotivo] = useState("");
    const [saving, setSaving] = useState(false);
    const disabled = !cap || cap.monto_disponible_real <= 0;

    const submit = async () => {
        const monto_num = parseFloat(monto);
        if (!monto_num || monto_num <= 0) {
            toast.error("Monto inválido"); return;
        }
        if (!cuentaId) { toast.error("Selecciona cuenta destino"); return; }
        if (!motivo || motivo.length < 3) {
            toast.error("Motivo requerido (mín 3 chars)"); return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(
                `/facilities/${facility.id}/disposiciones`,
                { monto: monto_num, moneda: facility.moneda,
                  cuenta_destino_id: cuentaId, motivo },
            );
            toast.success(`Disposición ${data.numero_disposicion} solicitada`);
            setOpen(false);
            setMonto(""); setCuentaId(""); setMotivo("");
            onDone?.();
        } catch (err) {
            toast.error("Error solicitando disposición", {
                description: err?.response?.data?.detail?.slice?.(0, 200) || "Verifica capacidad",
            });
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button disabled={disabled} data-testid="nueva-disposicion-btn">
                    <ArrowDownCircle className="h-4 w-4 mr-2" /> Nueva disposición
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Solicitar nueva disposición</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="p-3 rounded-sm bg-muted text-xs">
                        Disponible real ahora: <span className="font-semibold text-[color:var(--brand-exito)]">
                            {money(cap?.monto_disponible_real, facility.moneda)}
                        </span>
                        {cap?.monto_amortizado_reciclable > 0 && (
                            <span className="ml-2 text-[color:var(--brand-dark-gold)]">
                                (reciclable: {money(cap.monto_amortizado_reciclable, facility.moneda)})
                            </span>
                        )}
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Monto ({facility.moneda})
                        </label>
                        <Input type="number" value={monto} onChange={(e) => setMonto(e.target.value)}
                            placeholder="0.00" min="0.01" step="0.01"
                            className="mt-1"
                            data-testid="disposicion-monto-input" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Cuenta destino
                        </label>
                        <Select value={cuentaId} onValueChange={setCuentaId}>
                            <SelectTrigger className="mt-1" data-testid="disposicion-cuenta-select">
                                <SelectValue placeholder="Seleccionar..." />
                            </SelectTrigger>
                            <SelectContent>
                                {(cuentas || []).filter((c) => c.moneda === facility.moneda).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.titular_legal} ({c.moneda})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Motivo
                        </label>
                        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ej. redraw sobre pool reciclable para M4"
                            className="mt-1"
                            data-testid="disposicion-motivo-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button onClick={submit} disabled={saving}
                        data-testid="disposicion-submit-btn">
                        {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando…</>)
                                : (<><ArrowDownCircle className="h-4 w-4 mr-2" /> Solicitar</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AmortizacionDialog({ facility, onDone }) {
    const [open, setOpen] = useState(false);
    const [monto, setMonto] = useState("");
    const [fuente, setFuente] = useState("cobranza_creditos");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        const m = parseFloat(monto);
        if (!m || m <= 0) { toast.error("Monto inválido"); return; }
        setSaving(true);
        try {
            const { data } = await api.post(
                `/facilities/${facility.id}/aplicar-amortizacion`,
                { monto_bruto: m, fuente },
            );
            toast.success("Amortización aplicada", {
                description: `Principal: ${money(data.aplicacion?.find?.((a) => a.tipo === "principal")?.monto || 0, facility.moneda)}`,
            });
            setOpen(false); setMonto("");
            onDone?.();
        } catch (err) {
            toast.error("Error aplicando amortización", {
                description: err?.response?.data?.detail?.slice?.(0, 200),
            });
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="nueva-amortizacion-btn">
                    <ArrowUpCircle className="h-4 w-4 mr-2" /> Aplicar amortización
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Aplicar flujo de cobranza</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        Distribuye el flujo bruto en fees · intereses · reserva · principal.
                        Durante availability el principal va al pool reciclable (saldo NO baja).
                        Durante winddown reduce saldo.
                    </p>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Monto bruto ({facility.moneda})
                        </label>
                        <Input type="number" value={monto} onChange={(e) => setMonto(e.target.value)}
                            placeholder="0.00" step="0.01" min="0.01"
                            className="mt-1"
                            data-testid="amortizacion-monto-input" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Fuente
                        </label>
                        <Select value={fuente} onValueChange={setFuente}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cobranza_creditos">Cobranza créditos</SelectItem>
                                <SelectItem value="pago_manual">Pago manual</SelectItem>
                                <SelectItem value="waterfall_line_4">Waterfall línea 4</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button onClick={submit} disabled={saving}
                        data-testid="amortizacion-submit-btn">
                        {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Aplicando…</>)
                                : (<><ArrowUpCircle className="h-4 w-4 mr-2" /> Aplicar</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function RevolventeTab({ facility }) {
    const { hasRole } = useAuth();
    const [cap, setCap] = useState(null);
    const [disposiciones, setDisposiciones] = useState([]);
    const [amortizaciones, setAmortizaciones] = useState([]);
    const [cronograma, setCronograma] = useState([]);
    const [cuentas, setCuentas] = useState([]);
    const [loading, setLoading] = useState(true);

    const canOperate = useMemo(() => hasRole([
        "administrador_fiduciario", "tesoreria", "operaciones", "admin_ti",
    ]), [hasRole]);

    const load = async () => {
        if (!facility?.id) return;
        setLoading(true);
        try {
            const [c, d, a, cr, cta] = await Promise.all([
                api.get(`/facilities/${facility.id}/capacidad-disposicion`),
                api.get(`/facilities/${facility.id}/disposiciones`),
                api.get(`/facilities/${facility.id}/amortizaciones`),
                api.get(`/facilities/${facility.id}/cronograma`),
                api.get(`/cuentas?fideicomiso_id=${facility.fideicomiso_id}`)
                    .catch(() => ({ data: [] })),
            ]);
            setCap(c.data);
            setDisposiciones(d.data);
            setAmortizaciones(a.data);
            setCronograma(cr.data);
            setCuentas(cta.data);
        } catch (err) {
            toast.error("Error cargando revolvente", {
                description: err?.response?.data?.detail || "Verifica permisos",
            });
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility?.id]);

    if (loading) return (
        <div className="p-8 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando revolvente…
        </div>
    );

    const estado_color = ESTADO_COLOR[facility.estado] || "text-muted-foreground";

    return (
        <div className="space-y-6" data-testid="revolvente-tab">
            {/* Ciclo de vida */}
            <div className="rounded-md border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <h3 className="font-display text-lg">Ciclo de vida del facility</h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            Estado actual: <span className={`font-semibold ${estado_color}`}>
                                {ESTADO_LABELS[facility.estado] || facility.estado}
                            </span>
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={load}
                        data-testid="revolvente-refresh-btn">
                        <RefreshCw className="h-3 w-3 mr-1" /> Refrescar
                    </Button>
                </div>
                <CicloVidaTimeline facility={facility} />
            </div>

            {/* Capacidad */}
            <CapacidadGauge cap={cap} moneda={facility.moneda} />

            {/* Acciones */}
            {canOperate && (
                <div className="flex gap-2 flex-wrap">
                    <NuevaDisposicionDialog facility={facility} cap={cap}
                        cuentas={cuentas} onDone={load} />
                    <AmortizacionDialog facility={facility} onDone={load} />
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Disposiciones */}
                <div className="rounded-md border border-border bg-card overflow-hidden">
                    <div className="px-5 py-3 border-b border-border">
                        <h3 className="font-display text-lg flex items-center gap-2">
                            <ArrowDownCircle className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                            Disposiciones
                        </h3>
                        <p className="text-[11px] text-muted-foreground">
                            {disposiciones.length} totales
                        </p>
                    </div>
                    <table className="trf-table">
                        <thead><tr>
                            <th>Número</th><th>Fecha</th><th>Monto</th><th>Estado</th><th>Reciclable</th>
                        </tr></thead>
                        <tbody>
                            {disposiciones.map((d) => (
                                <tr key={d.id} data-testid={`disposicion-row-${d.id}`}>
                                    <td className="text-xs">{d.numero_disposicion}</td>
                                    <td className="tabular text-[11px] text-muted-foreground">
                                        {(d.fecha_efectiva || d.fecha_solicitud)?.slice(0, 10)}
                                    </td>
                                    <td className="tabular">{money(d.monto, d.moneda)}</td>
                                    <td>
                                        <span className={d.estado === "ejecutada" ? "badge-exito"
                                            : d.estado === "rechazada" ? "badge-error" : "badge-gold"}>
                                            {d.estado}
                                        </span>
                                    </td>
                                    <td className="tabular text-[11px]">
                                        {d.consumido_reciclable
                                            ? <span className="text-[color:var(--brand-dark-gold)]">
                                                {money(d.consumido_reciclable, d.moneda)}
                                              </span>
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                            {disposiciones.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-6 text-muted-foreground text-sm">
                                        Sin disposiciones registradas.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Amortizaciones */}
                <div className="rounded-md border border-border bg-card overflow-hidden">
                    <div className="px-5 py-3 border-b border-border">
                        <h3 className="font-display text-lg flex items-center gap-2">
                            <ArrowUpCircle className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                            Amortizaciones
                        </h3>
                        <p className="text-[11px] text-muted-foreground">
                            {amortizaciones.length} aplicadas
                        </p>
                    </div>
                    <table className="trf-table">
                        <thead><tr>
                            <th>Fecha</th><th>Bruto</th><th>Principal</th><th>Reciclable Δ</th><th>Estado</th>
                        </tr></thead>
                        <tbody>
                            {amortizaciones.map((a) => {
                                const principal = a.aplicacion?.find?.((x) => x.tipo === "principal")?.monto || 0;
                                return (
                                    <tr key={a.id} data-testid={`amortizacion-row-${a.id}`}>
                                        <td className="tabular text-[11px] text-muted-foreground">{a.fecha}</td>
                                        <td className="tabular">{money(a.monto_bruto_cobrado, facility.moneda)}</td>
                                        <td className="tabular">{money(principal, facility.moneda)}</td>
                                        <td className="tabular text-[color:var(--brand-dark-gold)]">
                                            {a.reciclable_pool_delta > 0
                                                ? "+" + money(a.reciclable_pool_delta, facility.moneda)
                                                : money(a.saldo_insoluto_delta, facility.moneda)}
                                        </td>
                                        <td>
                                            <span className="badge-gold text-[9px]">
                                                {a.estado_facility_al_aplicar}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            {amortizaciones.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-6 text-muted-foreground text-sm">
                                        Sin amortizaciones aplicadas.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Cronograma */}
            <div className="rounded-md border border-border bg-card overflow-hidden"
                data-testid="cronograma-chart">
                <div className="px-5 py-3 border-b border-border">
                    <h3 className="font-display text-lg flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                        Cronograma proyectado 12m
                    </h3>
                    <p className="text-[11px] text-muted-foreground">
                        {cronograma.length} items · intereses · amortizaciones · fees
                    </p>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                    <table className="trf-table">
                        <thead><tr>
                            <th>Fecha</th><th>Tipo</th><th>Descripción</th><th>Monto</th>
                        </tr></thead>
                        <tbody>
                            {cronograma.map((c, i) => (
                                <tr key={i}>
                                    <td className="tabular text-[11px] text-muted-foreground">{c.fecha}</td>
                                    <td>
                                        <span className="badge-gold text-[9px]">
                                            {c.tipo.replace("_", " ")}
                                        </span>
                                    </td>
                                    <td className="text-xs">{c.descripcion}</td>
                                    <td className="tabular">{money(c.monto, c.moneda)}</td>
                                </tr>
                            ))}
                            {cronograma.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-6 text-muted-foreground text-sm">
                                        Sin items proyectados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
